import React, { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser, updateUserPermissions, adminResetPassword, adminResetMfa } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { validatePassword, getPasswordStrength, MIN_PASSWORD_LENGTH } from '../utils/passwordPolicy';
import { 
  Users as UsersIcon, 
  MoreVertical, 
  Shield, 
  UserCheck, 
  UserX,
  Trash2,
  Search,
  UserPlus,
  Lock,
  KeyRound,
  Upload,
  Copy
} from 'lucide-react';
import { ShieldOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import Pagination from '../components/Pagination';
import BulkImportUsersDialog from '../components/BulkImportUsersDialog';
import DuplicateUsersDialog from '../components/DuplicateUsersDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const PERMISSION_LABELS = {
  dashboard: { label: 'Dashboard', actions: { view: 'Visualizar' } },
  licenses: { label: 'Licenças', actions: { view: 'Visualizar', create: 'Criar', edit: 'Editar', delete: 'Excluir' } },
  history: { label: 'Histórico', actions: { view: 'Visualizar' } },
  users: { label: 'Usuários', actions: { manage: 'Gerenciar' } },
  settings: { label: 'Configurações', actions: { manage: 'Gerenciar' } },
  export_import: { label: 'Exportar/Importar', actions: { use: 'Utilizar' } },
  notifications: { label: 'Notificações', actions: { send: 'Enviar' } },
};

const PasswordStrength = ({ password }) => {
  const strength = getPasswordStrength(password);
  return (
    <div className="space-y-1.5" data-testid="password-strength">
      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${strength.color} rounded-full transition-all duration-300`} style={{ width: strength.width }} />
      </div>
      <div className="flex justify-between items-center">
        <p className={`text-xs font-medium ${strength.textColor}`}>{strength.level}</p>
        <p className="text-xs text-gray-400">Min {MIN_PASSWORD_LENGTH} chars · maiúsculas, números, símbolos</p>
      </div>
    </div>
  );
};

const PermissionsDialog = ({ open, onOpenChange, user, onSave }) => {
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.permissions) {
      setPermissions(JSON.parse(JSON.stringify(user.permissions)));
    }
  }, [user]);

  const togglePermission = (module, action) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module]?.[action]
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(user.id, permissions);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#003366]" />
            Permissões - {user?.name}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Configure as permissões individuais deste usuário
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {Object.entries(PERMISSION_LABELS).map(([module, config]) => (
            <div key={module} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
              <h4 className="font-semibold text-gray-900 text-sm mb-3">{config.label}</h4>
              <div className="space-y-2">
                {Object.entries(config.actions).map(([action, label]) => (
                  <div key={`${module}-${action}`} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{label}</span>
                    <Switch
                      checked={permissions[module]?.[action] || false}
                      onCheckedChange={() => togglePermission(module, action)}
                      className="data-[state=checked]:bg-[#003366]"
                      data-testid={`perm-${module}-${action}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-white border-gray-300 text-gray-700">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#003366] hover:bg-[#002244]" data-testid="save-permissions-btn">
            {saving ? 'Salvando...' : 'Salvar Permissões'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [resetPwdDialogOpen, setResetPwdDialogOpen] = useState(false);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resettingPwd, setResettingPwd] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'usuario'
  });
  
  const { user: currentUser } = useAuth();

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUser(userId, { role: newRole });
      toast.success('Perfil atualizado (permissões redefinidas para o padrão do perfil)');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao atualizar perfil');
    }
  };

  const handleResetMfa = async (user) => {
    if (!window.confirm(`Remover o 2FA de ${user.name}? Ele poderá entrar apenas com a senha.`)) return;
    try {
      await adminResetMfa(user.id);
      toast.success('2FA removido com sucesso');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao resetar 2FA');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await updateUser(userId, { is_active: !isActive });
      toast.success(`Usuário ${!isActive ? 'ativado' : 'desativado'} com sucesso`);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id);
      toast.success('Usuário excluído com sucesso');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    setCreating(true);
    try {
      await createUser(newUser);
      toast.success('Usuário criado com sucesso');
      setCreateDialogOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'usuario' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setCreating(false);
    }
  };

  const handleSavePermissions = async (userId, permissions) => {
    try {
      await updateUserPermissions(userId, permissions);
      toast.success('Permissões atualizadas com sucesso');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao salvar permissões');
    }
  };

  const openPermissions = (user) => {
    setPermUser(user);
    setPermDialogOpen(true);
  };

  const openResetPassword = (user) => {
    setResetPwdUser(user);
    setResetPwdValue('');
    setResetPwdDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetPwdUser) return;
    const v = validatePassword(resetPwdValue);
    if (!v.ok) {
      toast.error(v.error);
      return;
    }
    setResettingPwd(true);
    try {
      await adminResetPassword(resetPwdUser.id, resetPwdValue);
      toast.success(`Senha de ${resetPwdUser.name} redefinida com sucesso`);
      setResetPwdDialogOpen(false);
      setResetPwdUser(null);
      setResetPwdValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setResettingPwd(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { label: 'Administrador', className: 'bg-rose-50 text-rose-700 border-rose-200' },
      gestor: { label: 'Gestor', className: 'bg-blue-50 text-[#003366] border-blue-200' },
      usuario: { label: 'Usuário', className: 'bg-gray-100 text-gray-600 border-gray-200' }
    };
    const { label, className } = config[role] || config.usuario;
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString('pt-BR'); } catch { return dateStr; }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const USERS_PAGE_SIZE = 10;
  const [usersPage, setUsersPage] = useState(1);
  useEffect(() => { setUsersPage(1); }, [search, users]);
  const pagedUsers = filteredUsers.slice((usersPage - 1) * USERS_PAGE_SIZE, usersPage * USERS_PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Gerenciamento de Usuários</h1>
          <p className="text-gray-500 mt-1">Administre os usuários e seus perfis de acesso</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setDuplicatesOpen(true)}
            variant="outline"
            className="bg-white border-gray-300 text-gray-700 font-semibold"
            data-testid="find-duplicates-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Localizar duplicados
          </Button>
          <Button 
            onClick={() => setBulkImportOpen(true)}
            variant="outline"
            className="bg-white border-gray-300 text-gray-700 font-semibold"
            data-testid="bulk-import-users-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar em massa
          </Button>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#003366] hover:bg-[#002244] font-semibold"
            data-testid="new-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Novo Usuário
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-lg"><UsersIcon className="w-6 h-6 text-[#003366]" /></div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">{users.length}</p>
              <p className="text-sm text-gray-500">Total de Usuários</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-50 rounded-lg"><UserCheck className="w-6 h-6 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">{users.filter(u => u.is_active).length}</p>
              <p className="text-sm text-gray-500">Usuários Ativos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-rose-50 rounded-lg"><Shield className="w-6 h-6 text-rose-600" /></div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">{users.filter(u => u.role === 'admin').length}</p>
              <p className="text-sm text-gray-500">Administradores</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-white border-gray-200 shadow-md">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar usuários..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              data-testid="search-users-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card className="bg-white border-gray-200 shadow-lg">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2 font-semibold">
            <UsersIcon className="w-5 h-5 text-[#003366]" />
            Lista de Usuários
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (<Skeleton key={i} className="h-16 bg-gray-100" />))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum usuário encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pagedUsers.map((user) => (
                <div 
                  key={user.id}
                  className={`flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:shadow-md transition-all ${!user.is_active ? 'opacity-60' : ''}`}
                  data-testid={`user-row-${user.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-10 w-10 shadow-md">
                      <AvatarFallback className={`${user.role === 'admin' ? 'bg-rose-500' : user.role === 'gestor' ? 'bg-[#003366]' : 'bg-gray-500'} text-white font-semibold`}>
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{user.name}</p>
                        {!user.is_active && (
                          <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-300 text-xs">Inativo</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="hidden sm:block">
                      <p className="text-xs text-gray-500">Criado em</p>
                      <p className="text-sm text-gray-700">{formatDate(user.created_at)}</p>
                    </div>

                    {user.id !== currentUser?.id ? (
                      <Select value={user.role} onValueChange={(value) => handleRoleChange(user.id, value)}>
                        <SelectTrigger className="w-[140px] bg-white border-gray-300 text-gray-700" data-testid={`role-select-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 shadow-xl">
                          <SelectItem value="admin">Administrador</SelectItem>
                          <SelectItem value="gestor">Gestor</SelectItem>
                          <SelectItem value="usuario">Usuário</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      getRoleBadge(user.role)
                    )}

                    {user.id !== currentUser?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-gray-500 hover:text-gray-700 hover:bg-gray-200" data-testid={`user-actions-${user.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white border-gray-200 shadow-xl">
                          <DropdownMenuItem
                            className="text-gray-700 hover:bg-gray-50 cursor-pointer"
                            onClick={() => openPermissions(user)}
                            data-testid={`permissions-btn-${user.id}`}
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            Permissões
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-gray-700 hover:bg-gray-50 cursor-pointer"
                            onClick={() => openResetPassword(user)}
                            data-testid={`reset-password-btn-${user.id}`}
                          >
                            <KeyRound className="w-4 h-4 mr-2" />
                            Redefinir senha
                          </DropdownMenuItem>
                          {user.mfa_enabled && (
                            <DropdownMenuItem
                              className="text-gray-700 hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleResetMfa(user)}
                              data-testid={`reset-mfa-${user.id}`}
                            >
                              <ShieldOff className="w-4 h-4 mr-2" />
                              Resetar 2FA
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-gray-200" />
                          <DropdownMenuItem
                            className="text-gray-700 hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleToggleActive(user.id, user.is_active)}
                            data-testid={`toggle-active-${user.id}`}
                          >
                            {user.is_active ? (
                              <><UserX className="w-4 h-4 mr-2" />Desativar</>
                            ) : (
                              <><UserCheck className="w-4 h-4 mr-2" />Ativar</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-gray-200" />
                          <DropdownMenuItem
                            className="text-rose-600 hover:bg-rose-50 cursor-pointer"
                            onClick={() => { setUserToDelete(user); setDeleteDialogOpen(true); }}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination page={usersPage} pageSize={USERS_PAGE_SIZE} total={filteredUsers.length} onPageChange={setUsersPage} testId="users-pagination" />
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200 shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Tem certeza que deseja excluir o usuário &ldquo;{userToDelete?.name}&rdquo;? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-gray-700 border-gray-300 hover:bg-gray-50">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-user-btn">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-white border-gray-200 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Novo Usuário</DialogTitle>
            <DialogDescription className="text-gray-500">Preencha os dados para criar um novo usuário no sistema.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700">Nome completo *</Label>
              <Input id="name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Ex: João Silva" className="bg-white border-gray-300 text-gray-900" data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">Email *</Label>
              <Input id="email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="Ex: joao@empresa.com" className="bg-white border-gray-300 text-gray-900" data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">Senha *</Label>
              <Input id="password" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`} className="bg-white border-gray-300 text-gray-900" data-testid="new-user-password" />
              {newUser.password && <PasswordStrength password={newUser.password} />}
            </div>
            <div className="space-y-2">
              <Label htmlFor="role" className="text-gray-700">Perfil de acesso *</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                <SelectTrigger className="bg-white border-gray-300 text-gray-700" data-testid="new-user-role">
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="gestor">Gestor</SelectItem>
                  <SelectItem value="usuario">Usuário</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="bg-white border-gray-300 text-gray-700">Cancelar</Button>
            <Button onClick={handleCreateUser} disabled={creating} className="bg-[#003366] hover:bg-[#002244]" data-testid="submit-new-user">
              {creating ? 'Criando...' : 'Criar Usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <PermissionsDialog
        open={permDialogOpen}
        onOpenChange={setPermDialogOpen}
        user={permUser}
        onSave={handleSavePermissions}
      />

      {/* Bulk Import Dialog */}
      <BulkImportUsersDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onImported={fetchUsers}
      />

      {/* Duplicate Users Dialog */}
      <DuplicateUsersDialog
        open={duplicatesOpen}
        onOpenChange={setDuplicatesOpen}
        onRemoved={fetchUsers}
      />

      {/* Reset Password Dialog */}
      <Dialog open={resetPwdDialogOpen} onOpenChange={setResetPwdDialogOpen}>
        <DialogContent className="bg-white border-gray-200 sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#003366]" />
              Redefinir senha
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Defina uma nova senha para o usuário <strong>{resetPwdUser?.name}</strong>. O usuário receberá esta senha e deve alterá-la no próximo acesso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-700">Nova senha *</Label>
              <Input
                type="password"
                value={resetPwdValue}
                onChange={(e) => setResetPwdValue(e.target.value)}
                placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`}
                className="bg-white border-gray-300 text-gray-900"
                data-testid="reset-password-input"
              />
              {resetPwdValue && <PasswordStrength password={resetPwdValue} />}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwdDialogOpen(false)} className="bg-white border-gray-300 text-gray-700">
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} disabled={resettingPwd} className="bg-[#003366] hover:bg-[#002244]" data-testid="confirm-reset-password-btn">
              {resettingPwd ? 'Redefinindo...' : 'Redefinir senha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
